import { ArrowRightIcon, CheckCircleIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { resolveWorkflowIcon } from './resolveWorkflowIcon';
import { OUTPUT_KIND_LABEL, formatEstimate } from './workflow-utils';

import { Button } from '@/components/ui/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import { ITEM_META, META_SEPARATOR } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import type { WorkflowTemplate } from '@/services/data/CampaignService';

interface WorkflowTemplatePreviewProps {
  template: WorkflowTemplate | null;
  /** Footer commit. When omitted, the footer is hidden. */
  onUse?: (templateId: string) => void;
  isAssigning?: boolean;
  /** When provided, renders a secondary "Attach to next message" CTA. */
  onAttach?: (templateId: string) => void;
  useLabel?: string;
  secondaryLabel?: string;
  onSecondary?: (templateId: string) => void;
}

function MetaRow({ template }: { template: WorkflowTemplate }): JSX.Element {
  const stepCount = template.steps?.length ?? 0;
  const reviewCount = (template.steps ?? []).filter((s) => s.requires_approval).length;
  const estimate = formatEstimate(template.estimate_minutes_min, template.estimate_minutes_max);

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', ITEM_META)}>
      <span>
        {stepCount} {stepCount === 1 ? 'step' : 'steps'}
      </span>
      {estimate && (
        <>
          <span className={META_SEPARATOR}>·</span>
          <span>{estimate}</span>
        </>
      )}
      {reviewCount > 0 && (
        <>
          <span className={META_SEPARATOR}>·</span>
          <span>
            {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
          </span>
        </>
      )}
    </div>
  );
}

function OutputLabel({ template }: { template: WorkflowTemplate }): JSX.Element | null {
  if (!template.output_kinds || template.output_kinds.length === 0) return null;
  const labels = template.output_kinds.map((k) => OUTPUT_KIND_LABEL[k]).filter(Boolean);
  if (labels.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', ITEM_META)}>
      <span>Creates</span>
      <span className="font-medium text-foreground/85">{labels.join(', ')}</span>
    </div>
  );
}

/**
 * Workflow preview body for the WorkflowLibraryDialog right pane.
 *
 * Single-column: header (title + description + meta) → step list →
 * footer (Attach + Use this).
 */
export const WorkflowTemplatePreview: React.FC<WorkflowTemplatePreviewProps> = ({
  template,
  onUse,
  isAssigning,
  onAttach,
  useLabel = 'Use this',
  secondaryLabel,
  onSecondary,
}) => {
  if (!template) {
    return (
      <Empty className="border-0 flex-1">
        <EmptyHeader>
          <EmptyTitle>Pick a workflow</EmptyTitle>
          <EmptyDescription>Pick a workflow on the left to see what it does.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Header template={template} />
      <SingleBody template={template} />
      {onUse && (
        <Footer
          template={template}
          onUse={onUse}
          isAssigning={isAssigning}
          onAttach={onAttach}
          useLabel={useLabel}
          secondaryLabel={secondaryLabel}
          onSecondary={onSecondary}
        />
      )}
    </div>
  );
};

function Header({ template }: { template: WorkflowTemplate }): JSX.Element {
  const WorkflowIcon = resolveWorkflowIcon(template.metadata?.icon_id);
  return (
    <header className="space-y-2 border-b border-border/40 px-5 pb-4 pt-5 sm:px-6">
      <div className="flex items-center gap-2">
        <WorkflowIcon weight="regular" className="size-4 shrink-0 text-muted-foreground/70" />
        <h2 className="text-base font-medium leading-snug text-foreground">{template.name}</h2>
      </div>
      {template.description && (
        <p className="text-sm leading-relaxed text-muted-foreground">{template.description}</p>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-0.5">
        <MetaRow template={template} />
        <OutputLabel template={template} />
      </div>
    </header>
  );
}

function Footer({
  template,
  onUse,
  isAssigning,
  onAttach,
  useLabel,
  secondaryLabel,
  onSecondary,
}: {
  template: WorkflowTemplate;
  onUse: (id: string) => void;
  isAssigning?: boolean;
  onAttach?: (id: string) => void;
  useLabel: string;
  secondaryLabel?: string;
  onSecondary?: (id: string) => void;
}): JSX.Element {
  return (
    <footer className="flex items-center justify-between gap-2 border-t border-border/40 px-5 py-3 sm:px-6">
      <div className="flex items-center gap-2">
        {onSecondary && secondaryLabel && (
          <Button
            variant="flat"
            size="sm"
            onClick={() => onSecondary(template.id)}
            disabled={isAssigning}
          >
            {secondaryLabel}
          </Button>
        )}
        {onAttach && (
          <Button
            variant="flat"
            size="sm"
            onClick={() => onAttach(template.id)}
            disabled={isAssigning}
          >
            Attach to next message
          </Button>
        )}
      </div>
      <Button variant="neutral" size="sm" onClick={() => onUse(template.id)} disabled={isAssigning}>
        {isAssigning ? (
          <>
            <Spinner className="size-3.5" />
            Starting…
          </>
        ) : (
          <>
            {useLabel}
            <ArrowRightIcon className="size-3.5" weight="regular" aria-hidden />
          </>
        )}
      </Button>
    </footer>
  );
}

function SingleBody({ template }: { template: WorkflowTemplate }): JSX.Element {
  return (
    <ol className="flex-1 overflow-y-auto px-5 py-5 space-y-4 sm:px-6">
      {(template.steps ?? []).map((step, idx) => (
        <li key={step.step_id} className="flex items-start gap-3.5">
          <span aria-hidden className="shrink-0 pt-0.5 w-5 text-detail text-muted-foreground/55">
            {idx + 1}.
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-medium leading-snug text-foreground">{step.action}</p>
              {step.requires_approval && (
                <CheckCircleIcon
                  className="size-3.5 shrink-0 text-skill/70"
                  weight="duotone"
                  aria-label="Requires your review"
                />
              )}
            </div>
            {(step.uses || step.creates) && (
              <p className={cn('mt-1', ITEM_META)}>
                {step.uses && (
                  <>
                    <span className="text-muted-foreground/60">Takes</span> {step.uses}
                  </>
                )}
                {step.uses && step.creates && (
                  <span className={cn('mx-1.5', META_SEPARATOR)}>·</span>
                )}
                {step.creates && (
                  <>
                    <span className="text-muted-foreground/60">Makes</span> {step.creates}
                  </>
                )}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
